import React, { useCallback } from 'react';
import { i18n } from 'utils/setupTranslations';
import {
    Group,
    LoopOutlined,
    SoundOutlined,
    IconButton,
    ButtonSize,
} from '@landr/maestro';
import { ModifiersPayload } from 'controllers/SearchController/types';
import { FiltersParams } from 'types/params';
import { SampleType } from 'types/enums';

interface TypeButtonProps {
    addFilter: (modifiers: ModifiersPayload[]) => void;
    clearByFilter: (type: FiltersParams) => void;
    code: string;
    isChecked: boolean;
    buttonSize: ButtonSize;
    isDisabled?: boolean;
}

const useTypeButton = ({
    addFilter,
    clearByFilter,
    code,
    isChecked,
}: Omit<TypeButtonProps, 'buttonSize'>) => {
    let icon;

    switch (code) {
        case SampleType.Loop:
            icon = LoopOutlined;
            break;

        case SampleType.OneShot:
            icon = SoundOutlined;
            break;

        default:
            break;
    }

    const label = i18n._(code);

    const handleOnClick = useCallback(() => {
        if (isChecked) {
            clearByFilter(FiltersParams.type);
        } else {
            addFilter([[FiltersParams.type, code]]);
        }
    }, [addFilter, clearByFilter, isChecked, code]);

    return { icon, label, handleOnClick };
};

const TypeButton = ({
    addFilter,
    clearByFilter,
    code,
    isChecked,
    buttonSize,
    isDisabled = false,
}: TypeButtonProps) => {
    const { icon, label, handleOnClick } = useTypeButton({
        addFilter,
        clearByFilter,
        code,
        isChecked,
    });

    if (!label || !icon) {
        return null;
    }

    return (
        <Group.Item>
            <IconButton
                onClick={handleOnClick}
                icon={icon}
                label={label}
                isRounded
                variant={isChecked ? 'secondary' : 'ghost'}
                size={buttonSize}
                isDisabled={isDisabled}
            />
        </Group.Item>
    );
};

export default TypeButton;
